import * as React from "react";
import {
  NavigationGroup,
  PatientCaseGrouping,
} from "../../../context/diagnosticAgent";
import { ChiefComplaintPage } from "../chiefComplaintPage";
import { DdPanel } from "../panels/DDPanel";
import { AgentRoute } from "./agentRoutes";
import { CasesListsRoute } from "../Cases/CasesListsRoute";
import { ExpandedDisease } from "../rosebud/expandedDisease";
import { DataEntrySection } from "../sections/DataEntrySection";
import { ClinicalFeaturesSection } from "../sections/ClinicalFeaturesSection";
import { BackgroundSection } from "../sections/BackgroundSection";
import { WorkupSection } from "../sections/WorkupSection";
import { MobilePatientCase } from "../rosebud/mobilePatientCase";
import { VitalSignsSection } from "../sections/VitalSignsSection";
import { AgeAndGender } from "../AgeAndGender";
import { HistoryAndPhysical } from "../historyAndPhysical";
import { PatientDiseaseContainer } from "../PatientDiseaseContainer";
import { WelcomeScreen } from "../landingPage/welcomeScreen";
import { ExportData } from "../exportData/exportData";
import { WizardTermOfService } from "../wizard/wizardTermOfService";
import { Plan } from "../plan";
import { LoginPage } from "../../userManagement/pages/loginPage";
import { SignUpPage } from "../../userManagement/pages/signupPage";
import { ForgotPassword } from "../../userManagement/pages/forgotPassword";
import { EditPanelPage } from "../editPanel/editPanelPage";
import { PatientFirstSummary } from "../patientFirstSummary/patientFirstSummary";
import { ReviewOfSystemsSection } from "../sections/ReviewOfSystemsSection";

type DisplayState = {
  inWizard: boolean;
  isMobile: boolean;
};

export interface NavigationLink {
  route: AgentRoute | string;
  displayText?: string;
  icon?: React.ReactElement;
}

export type RenderByDisplayState = (displayState: DisplayState) => boolean;

export type BottomNavigationSettingSwitch = (
  displayState: DisplayState,
) => { primary: NavigationLink; secondary?: NavigationLink };

export const RouteSettingToNavigationLink = (
  routeSetting: RosebudRouteSetting,
) => {
  return routeSetting as NavigationLink;
};

export const NavigationLinkToRouteSetting = (
  navigationLink: NavigationLink,
) => {
  return RosebudRoutesSettings.find((rs) => rs.route === navigationLink.route);
};

/***
 * Use this object to define the steps of patient case definition
 * use create section function to create new patient steps
 */
export interface RosebudRouteSetting extends NavigationLink {
  displayText: string;
  /*** An icon to be rendered next to the display name of the route */
  icon?: React.ReactElement;
  route: AgentRoute | string;
  component: React.ComponentType<any>;
  navigationGroup?: NavigationGroup;
  hidePatientCase: RenderByDisplayState;
  hideTopBar?: RenderByDisplayState;
  grouping?: PatientCaseGrouping;
  isWizardStep: boolean;
  /***
   * Does this route needs the DD to be calculated from server or can the
   * case be stored in state without the results yet?
   */
  isDDCalculationRequired?: boolean;
  /***
   * Some steps appear only in mobile, and some do exactly the same, setting this to true
   * won't render the route in desktop and vice versa
   */
  mobileOnly: boolean;
  desktopOnly: boolean;
  /***
   * For mobile only routes -> where will we land when we load the route in desktop?
   */
  toDesktopRedirectRoute?: AgentRoute;
  toMobileRedirectRoute?: AgentRoute;
  /***
   * This allows routes to make use of the rousbudBottomNavigation
   * by settting to which route and what label they should display
   */
  renderBottomNavigation?: BottomNavigationSettingSwitch;
  /**
   * By default all routes has to have the user agreeement accepted to render,
   * accept of-cours the user agreement itself, and some special routes
   * (such as welcome screen and more future routes - set to false by default)
   */
  bypassUserAgreement?: boolean;
}

export type CreateRouteSettings = {
  route: AgentRoute | string;
  displayText?: string;
  grouping?: PatientCaseGrouping;
  component?: React.ComponentType<any>;
  hidePatientCase?: RenderByDisplayState;
  wizardStep?: boolean;
  isDDCalculationRequired?: boolean;
  mobileOnly?: boolean;
  desktopOnly?: boolean;
  toDesktopRedirectRoute?: AgentRoute;
  toMobileRedirectRoute?: AgentRoute;
  icon?: React.ReactElement;
  navigationGroup?: NavigationGroup;
  renderBottomNavigation?: BottomNavigationSettingSwitch;
  bypassUserAgreement?: boolean;
  hideTopBar?: RenderByDisplayState;
};

export const EmptyComponent = () => null;

const always = () => true;

const never = () => false;

//       -------------     Bottom Navigation Settings    -----------

const goBackToPatient = () => ({
  primary: { route: AgentRoute.PatientChart },
});

const defaultDD = () => ({ primary: { route: AgentRoute.DD } });

const updateDiagnosis = () => ({
  primary: {
    route: AgentRoute.DD,
    displayText: "Update",
    icon: <i className={"fas fa-stethoscope"} />,
  },
});

const makeMobileOnlyNavigation = (button: BottomNavigationSettingSwitch) => (
  state,
) => (state.isMobile ? button(state) : null);

const mobileOnlyGoToPatient = makeMobileOnlyNavigation(goBackToPatient);

const mobileOnlyUpdateDiagnosis = makeMobileOnlyNavigation(updateDiagnosis);

//        -------  End of Bottom Navigation Settings    ----------

export function createRouteSetting({
  displayText,
  grouping,
  route,
  component,
  hidePatientCase,
  wizardStep,
  isDDCalculationRequired,
  mobileOnly,
  desktopOnly,
  icon,
  toDesktopRedirectRoute,
  toMobileRedirectRoute,
  navigationGroup,
  renderBottomNavigation,
  bypassUserAgreement,
  hideTopBar,
}: CreateRouteSettings): RosebudRouteSetting {
  return {
    displayText: displayText ?? route,
    grouping: grouping ?? null,
    route,
    component: component ?? EmptyComponent,
    hidePatientCase: hidePatientCase || never,
    isWizardStep: wizardStep || false,
    isDDCalculationRequired: isDDCalculationRequired || false,
    mobileOnly: mobileOnly || false,
    desktopOnly: desktopOnly || false,
    icon: icon,
    renderBottomNavigation: renderBottomNavigation || null,
    toDesktopRedirectRoute: toDesktopRedirectRoute || null,
    toMobileRedirectRoute: toMobileRedirectRoute || null,
    navigationGroup: navigationGroup,
    bypassUserAgreement: bypassUserAgreement || false,
    hideTopBar: hideTopBar || never,
  };
}

/***
 * This is where we define our routes in the app, by providing some more meta-data
 * on the way they are rendered, we can than know what is their order in the initialization steps
 * and how to call them for humans to understand
 */
export const RosebudRoutesSettings = [
  createRouteSetting({
    displayText: "Welcome",
    route: AgentRoute.Welcome,
    component: WelcomeScreen,
    hidePatientCase: always,
    bypassUserAgreement: true,
  }),
  createRouteSetting({
    displayText: "Terms of Service",
    route: AgentRoute.TermsOfService,
    component: WizardTermOfService,
    hidePatientCase: always,
    wizardStep: true,
    bypassUserAgreement: true,
    renderBottomNavigation: defaultDD,
  }),
  createRouteSetting({
    displayText: "Age and Gender",
    route: AgentRoute.AgeAndGender,
    component: AgeAndGender,
    navigationGroup: NavigationGroup.PATIENT,
    renderBottomNavigation: mobileOnlyGoToPatient,
    wizardStep: true,
  }),
  createRouteSetting({
    displayText: "Chief Complaint",
    route: AgentRoute.ChiefComplaint,
    component: ChiefComplaintPage,
    navigationGroup: NavigationGroup.PATIENT,
    mobileOnly: true,
    wizardStep: true,
    toDesktopRedirectRoute: AgentRoute.AgeAndGender,
  }),
  createRouteSetting({
    displayText: "Vital Signs",
    route: AgentRoute.VitalSigns,
    component: VitalSignsSection,
    navigationGroup: NavigationGroup.PATIENT,
    grouping: PatientCaseGrouping.VITAL_SIGNS,
    wizardStep: true,
  }),
  createRouteSetting({
    displayText: "Data Entry",
    route: `${AgentRoute.DataEntry}`,
    component: DataEntrySection,
    navigationGroup: NavigationGroup.PATIENT,
    mobileOnly: true,
    wizardStep: false,
    toDesktopRedirectRoute: AgentRoute.ClinicalFeatures,
  }),

  createRouteSetting({
    displayText: "Review Of Systems",
    route: AgentRoute.ReviewOfSystems,
    component: ReviewOfSystemsSection,
    navigationGroup: NavigationGroup.PATIENT,
    grouping: PatientCaseGrouping.REVIEW_OF_SYSTEMS,
    desktopOnly: false,
    wizardStep: true,
    toMobileRedirectRoute: AgentRoute.DataEntry,
  }),

  createRouteSetting({
    displayText: "Physical Examination",
    route: AgentRoute.ClinicalFeatures,
    component: ClinicalFeaturesSection,
    navigationGroup: NavigationGroup.PATIENT,
    grouping: PatientCaseGrouping.CLINICAL_FEATURES,
    desktopOnly: false,
    wizardStep: true,
    toMobileRedirectRoute: AgentRoute.DataEntry,
  }),

  createRouteSetting({
    displayText: "Past Medical History",
    route: AgentRoute.Background,
    component: BackgroundSection,
    navigationGroup: NavigationGroup.PATIENT,
    grouping: PatientCaseGrouping.BACKGROUND,
    desktopOnly: false,
    wizardStep: true,
    toMobileRedirectRoute: AgentRoute.DataEntry,
  }),
  createRouteSetting({
    displayText: "Workup",
    route: AgentRoute.Workup,
    component: WorkupSection,
    navigationGroup: NavigationGroup.PATIENT,
    grouping: PatientCaseGrouping.WORKUP,
    desktopOnly: false,
    wizardStep: true,
    toMobileRedirectRoute: AgentRoute.DataEntry,
  }),
  createRouteSetting({
    displayText: "Edit Clinical Findings",
    route: AgentRoute.EditClinicalFeatures,
    component: ClinicalFeaturesSection,
    navigationGroup: NavigationGroup.PATIENT,
    mobileOnly: true,
    renderBottomNavigation: mobileOnlyGoToPatient,
    toDesktopRedirectRoute: AgentRoute.ClinicalFeatures,
  }),
  createRouteSetting({
    displayText: "Edit Past Medical History",
    route: AgentRoute.EditBackground,
    component: BackgroundSection,
    navigationGroup: NavigationGroup.PATIENT,
    mobileOnly: true,
    renderBottomNavigation: mobileOnlyGoToPatient,
    toDesktopRedirectRoute: AgentRoute.Background,
  }),
  createRouteSetting({
    displayText: "Edit Workup",
    route: AgentRoute.EditWorkup,
    component: WorkupSection,
    navigationGroup: NavigationGroup.PATIENT,
    mobileOnly: true,
    renderBottomNavigation: mobileOnlyGoToPatient,
    toDesktopRedirectRoute: AgentRoute.Workup,
  }),
  createRouteSetting({
    displayText: "Done",
    route: AgentRoute.DD,
    component: DdPanel,
    navigationGroup: NavigationGroup.DIAGNOSIS,
    isDDCalculationRequired: true,
    icon: <i className={"fas fa-stethoscope"} />,
  }),
  createRouteSetting({
    displayText: "Saved Cases",
    route: AgentRoute.Cases,
    component: CasesListsRoute,
  }),
  createRouteSetting({
    displayText: "More Info",
    route: `${AgentRoute.ExpandedDisease}/:qfid`,
    component: ExpandedDisease,
    mobileOnly: true,
    hideTopBar: always,
    hidePatientCase: always,
    toDesktopRedirectRoute: AgentRoute.DD,
  }),
  createRouteSetting({
    displayText: "Back to Patient",
    route: AgentRoute.PatientChart,
    component: MobilePatientCase,
    navigationGroup: NavigationGroup.PATIENT,
    mobileOnly: true,
    icon: <i className="fas fa-arrow-left"></i>,
    toDesktopRedirectRoute: AgentRoute.DD,
  }),
  createRouteSetting({
    displayText: "Plan",
    route: AgentRoute.RecommendedPlan,
    component: Plan,
    navigationGroup: NavigationGroup.PLAN,
    mobileOnly: true,
    toDesktopRedirectRoute: AgentRoute.DD,
  }),
  createRouteSetting({
    displayText: "Expanded",
    route: `${AgentRoute.Expanded}/:featureId`,
    component: PatientDiseaseContainer,
    navigationGroup: NavigationGroup.DIAGNOSIS,
    hidePatientCase: always,
  }),
  createRouteSetting({
    displayText: "Edit Panel",
    route: `${AgentRoute.EditPanel}/:panelId`,
    component: EditPanelPage,
    navigationGroup: NavigationGroup.PLAN,
    renderBottomNavigation: mobileOnlyUpdateDiagnosis,
    hideTopBar: always,

    hidePatientCase: always,
  }),

  createRouteSetting({
    displayText: "PatientFirstSummary",
    route: AgentRoute.PatientFirstSummary,
    component: PatientFirstSummary,
    hidePatientCase: always,
    bypassUserAgreement: true,
  }),

  createRouteSetting({
    displayText: "Physical And History",
    route: AgentRoute.HistoryAndPhysical,
    component: HistoryAndPhysical,
    navigationGroup: NavigationGroup.DIAGNOSIS,
    mobileOnly: true,
    renderBottomNavigation: mobileOnlyUpdateDiagnosis,
    toDesktopRedirectRoute: AgentRoute.DD,
  }),
  createRouteSetting({
    displayText: "Export",
    route: AgentRoute.Export,
    component: ExportData,
    navigationGroup: NavigationGroup.PLAN,
  }),
  createRouteSetting({
    displayText: "Login",
    route: AgentRoute.Login,
    component: LoginPage,
    hidePatientCase: always,
    hideTopBar: always,
    bypassUserAgreement: true,
  }),
  createRouteSetting({
    displayText: "Signup",
    route: AgentRoute.Signup,
    component: SignUpPage,
    hidePatientCase: always,
    hideTopBar: always,
    bypassUserAgreement: true,
  }),
  createRouteSetting({
    displayText: "Reset Password",
    route: AgentRoute.PasswordReset,
    component: ForgotPassword,
    hidePatientCase: always,
    hideTopBar: always,
    bypassUserAgreement: true,
  }),
] as RosebudRouteSetting[];
